import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../common/axiosInstance";
import { DotLoader } from "../../components/loader/Loader";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  themeQuartz,
  ColDef,
  ICellRendererParams,
  GridReadyEvent,
} from "ag-grid-community";
import images from "../../assets/images";
import { usePermissions } from "../../hooks/usePermission";
import { EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING } from "../../constants/modules";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

interface MISChecklist {
  _id: string;
  name: string;
  premisesId?: string;
  premisesName?: string;
  date?: string;
  createdAt: string;
  updatedAt: string;
}

const MISChecklistList: React.FC = () => {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState<MISChecklist[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { canCreate, canRead } = usePermissions();

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get("/api/fire-safety-mis-checklists");
      const data = response.data?.data || response.data || [];
      setChecklists(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error("Error fetching checklists:", error);
      setError(error.response?.data?.error || error.response?.data?.message || "Failed to fetch checklists");
      // If API doesn't exist yet, set empty array
      if (error.response?.status === 404 || error.response?.status === 500) {
        setChecklists([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate("/safety-equipment/fire-safety-mis-checklist/new");
  };

  const handleEdit = (id: string) => {
    navigate(`/safety-equipment/fire-safety-mis-checklist/${id}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const formatDateOnly = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this checklist?")) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/fire-safety-mis-checklists/${id}`);
      await fetchChecklists();
    } catch (error: any) {
      console.error("Error deleting checklist:", error);
      alert(error.response?.data?.error || error.response?.data?.message || "Failed to delete checklist");
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Checklist Name",
        sortable: true,
        flex: 2,
        minWidth: 200,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
        } as any,
      },
      {
        field: "premisesName",
        headerName: "Premises",
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => (
          <div className="flex items-center gap-1">
            {params.value ? (
              <span>{params.value}</span>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
        ),
      },
      {
        field: "date",
        headerName: "Date",
        sortable: true,
        flex: 1,
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => (
          <div className="flex items-center gap-1 justify-center">
            {params.value ? (
              <span>{formatDateOnly(params.value)}</span>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </div>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created",
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => formatDate(params.value),
      },
      {
        field: "updatedAt",
        headerName: "Last Updated",
        sortable: true,
        flex: 1.5,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => formatDate(params.value),
      },
      {
        field: "action",
        headerName: "Actions",
        sortable: false,
        flex: 1.2,
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: {
          padding: "12px 8px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } as any,
        cellRenderer: (params: ICellRendererParams) => (
          <div className="flex gap-x-3 justify-center items-center">
            <button
              onClick={() => handleEdit(params.data._id)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="View/Edit Checklist"
            >
              <img className="w-5 h-5" src={images.viewIcon} alt="View" />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-50 rounded transition-colors text-red-600"
              title="Delete Checklist"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
    }),
    []
  );

  const onGridReady = useCallback((e: GridReadyEvent) => {
    e.api.sizeColumnsToFit();
  }, []);

  const cancreate = canCreate(EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING);
  const canread = canRead(EQUIPMENT_MANAGEMENT_MAINTENANCE_SCHEDULING_AND_TRACKING);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <DotLoader />
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-4">
      <div className="w-full mt-20 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Fire Safety MIS Checklist Management
            </h2>
            <p className="text-gray-600">
              View, create, and manage your Fire Safety MIS checklists
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/safety-equipment")}
              className="flex items-center gap-2 py-2 px-4 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
            {cancreate && <button
              onClick={handleCreateNew}
              className="flex items-center gap-2 py-2 px-4 bg-[#00A8A5] text-white rounded hover:bg-[#008A87] transition-colors shadow-lg hover:shadow-xl"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create New Checklist
            </button>}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6">
        {checklists.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Checklists Found
            </h3>
            <p className="text-gray-500 mb-6">
              Get started by creating your first Fire Safety MIS checklist
            </p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 py-2 px-6 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Your First Checklist
            </button>
          </div>
        ) : (
          <div className="ag-theme-alpine w-full h-[650px] border-0 rounded-lg shadow-lg">
            <AgGridReact
              rowData={checklists}
              columnDefs={columnDefs}
              theme={gridTheme}
              headerHeight={65}
              rowHeight={60}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50]}
              paginationAutoPageSize={false}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onRowDoubleClicked={(params) => handleEdit(params.data._id)}
              rowSelection="single"
              suppressRowClickSelection={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MISChecklistList;


